import { extractToken } from '@atlassian/color-picker';

/**
 * Whether a stored text colour is one this system may re-derive.
 *
 * The distinction is **seeded default vs admin choice**, and the two are told
 * apart by their *shape*:
 *
 *   • **Auto-managed** — a design-token reference. Every element manifest seeds
 *     `token('color.text')` / `token('color.text.inverse')`, i.e.
 *     `var(--ds-text, …)` / `var(--ds-text-inverse, …)`, and the legacy
 *     `--hub-primary-text-color` var is itself a readable black/white. Nothing
 *     stored counts too — the element is showing a derived default.
 *   • **Explicit** — anything else, including a bare `#FFFFFF` or `#000000`.
 *
 * That split matters because the colour picker emits a **raw hex**: its
 * `knownColors` are lowercased literals (see `createBuiltInColorMap`), so
 * choosing the white swatch stores `#ffffff` while the seeded white is
 * `var(--ds-text-inverse, …)`. Treating a bare hex as auto-managed made an
 * explicitly chosen white unusable — it was immediately re-derived back to dark
 * on a light background, so the pick appeared not to apply at all.
 *
 * Matching is done on the *string*, never through `extractColorCode`, because
 * that resolves vars against `document.documentElement` — where the hub
 * palette does not live (the provider emits it on its own wrapper div), so it
 * silently yields `''` and every comparison collapses to black.
 */

const AUTO_MANAGED_TOKENS = new Set(['--ds-text', '--ds-text-inverse', '--hub-primary-text-color']);

export const isAutoManagedTextColor = (value: string | undefined): boolean => {
	const trimmed = value?.trim();

	if (!trimmed) {
		// Nothing stored means the element is showing a derived default, which is
		// exactly the case we want to keep deriving.
		return true;
	}

	// `extractToken` returns the custom-property name for a `var(...)` string and
	// the input untouched otherwise, so this covers both token and var forms
	// without touching the DOM.
	return AUTO_MANAGED_TOKENS.has(extractToken(trimmed));
};
